import { z } from "zod";
import { dateFormat } from "./date";
import emojiRegex from "emoji-regex";

/**
 * Capitalizes the first letter of a string and converts the rest to lowercase.
 *
 * Example:
 * ```typescript
 * upperCaseFirstLetter("Crême Brulée"); // Returns "Crême brulée"
 * ```
 */
export const upperCaseFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/** Uppercases the first character and leaves the rest unchanged. */
export const capitalizeFirstLetter = (value?: string): string => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Removes diacritical marks (accents) from a string.
 *
 * Example:
 * ```typescript
 * removeAccents("Crême Brülée"); // Returns "Creme Brulee"
 * ```
 *
 * Source: https://github.com/krisk/Fuse/issues/415
 */
export const removeAccents = function (text: string): string {
  return text.normalize("NFD").replace(/[\u0300-\u036F]/g, "");
};

/**
 * Normalizes a string by removing accents and converting it to lowercase.
 *
 * Example:
 * ```typescript
 * normalizeString("Crême Brülée"); // Returns "creme brulee"
 * ```
 *
 * Source: https://stackoverflow.com/a/37511463
 */
export const normalizeString = (text: string): string => removeAccents(text).toLowerCase();

/**
 * Sanitizes a filename for filesystem storage by:
 * - Removing accents
 * - Converting to lowercase
 * - Replacing spaces with underscores
 * - Removing special characters (except underscores and dashes)
 * - Preserving file extension
 *
 * Optionally accepts a transform function to modify the base name before sanitization.
 * The transform is applied to the base name (without extension), then the result is
 * sanitized and the extension is reattached.
 *
 * @param filename - The original filename to sanitize
 * @param transform - Optional function to transform the base name before sanitization
 * @returns A filesystem-safe filename with extension preserved
 *
 * @example
 * ```typescript
 * sanitizeFileName("Photo d'Identité.jpg")
 * // Returns: "photo_didentite.jpg"
 *
 * sanitizeFileName("photo.jpg", (base) => `prefix_${base}_${Date.now()}`)
 * // Returns: "prefix_photo_1699999999.jpg"
 *
 * sanitizeFileName("photo.backup.jpg")
 * // Returns: "photo_backup.jpg" (only last segment treated as extension)
 * ```
 */
export const sanitizeFileName = (filename: string, transform?: (baseName: string) => string): string => {
  // Handle empty string
  if (!filename) return "";

  // Extract extension (last dot segment)
  const lastDotIndex = filename.lastIndexOf(".");
  let baseName: string;
  let extension: string;

  if (lastDotIndex === -1) {
    // No extension
    baseName = filename;
    extension = "";
  } else if (lastDotIndex === 0) {
    // Hidden file or extension-only (.gitignore, .jpg)
    baseName = filename.slice(1);
    extension = "";
  } else {
    // Normal file with extension
    baseName = filename.slice(0, lastDotIndex);
    extension = filename.slice(lastDotIndex + 1);
  }

  // Apply optional transform to base name
  if (transform) {
    baseName = transform(baseName);
  }

  // Sanitize base name
  const sanitizedBase = normalizeString(baseName)
    .replace(/[\s.]/g, "_") // Replace spaces and dots with underscores
    .replace(/[^a-z\d_-]/g, ""); // Remove all non-alphanumeric except underscore and dash

  // Sanitize extension (same rules but no need for underscores in extension)
  const sanitizedExtension = extension ? normalizeString(extension).replace(/[^a-z\d]/g, "") : "";

  // Recombine with dot if extension exists
  return sanitizedExtension ? `${sanitizedBase}.${sanitizedExtension}` : sanitizedBase;
};

export const isEmail = (text: string): boolean => {
  /**
   * Regular expression to check if a string is in a basic email format.
   *
   * The pattern ensures:
   * - A sequence of characters before the '@' symbol.
   * - An '@' symbol.
   * - A sequence of characters followed by a dot ('.') for the domain.
   * - A sequence of characters for the top-level domain (e.g., '.com').
   *
   * Note: This regex only checks the format, not the validity of the domain.
   */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(text);
};

export const cleanEmail = (email: string | null | undefined): string | undefined => {
  if (!email) return undefined;

  return email
    .replaceAll(/[,:()"/\s]/g, "") // Some special character invalid for email
    .trim()
    .toLowerCase();
};

export const validateEmail = (email: string) => {
  const emailSchema = z.email();
  return emailSchema.safeParse(email).success;
};

/** Cleans and validates an email; returns undefined when missing or invalid. */
export const toOptionalValidEmail = (email: string | null | undefined): string | undefined => {
  const cleanedEmail = cleanEmail(email);
  if (!cleanedEmail || !validateEmail(cleanedEmail)) {
    return undefined;
  }
  return cleanedEmail;
};

/**
 * Strips stray newlines sometimes present in stored URLs (e.g. `%0A`).
 */
export const sanitizeFileUrl = (url: string): string => {
  return url.replace(/%0A/gi, "").replace(/[\r\n]+/g, "");
};

/**
 * Permet d'ouvrir le PDF dans un `iframe` sans interface du viewer.
 *
 * Note: l'UI exacte (toolbar/nav/scrollbar) dépend du navigateur.
 * Les fragments ci-dessous sont ceux supportés par la plupart des viewers PDF.
 *
 * cf https://pdfobject.com/pdf/pdf_open_parameters_acro8.pdf#page=7
 */
export const getPdfUrlForIframe = function (url: string) {
  const sanitized = sanitizeFileUrl(url);
  if (!sanitized.length) return undefined;

  const [baseUrl, existingFragment = ""] = sanitized.split("#");

  const additions = [
    // Hide left navigation pane (thumbnails / outline)
    "navpanes=0",
  ];

  const hasKey = (key: string) => new RegExp(`(?:^|&)${key}=`).test(existingFragment);
  const filteredAdditions = additions.filter((pair) => {
    const key = pair.split("=")[0] ?? "";
    return key ? !hasKey(key) : true;
  });

  if (!filteredAdditions.length) return sanitized;

  const separator = existingFragment.length ? "&" : "";
  return `${baseUrl}#${existingFragment}${separator}${filteredAdditions.join("&")}`;
};

export const removeEmojis = (text: string): string => {
  // This emoji regex already has the global flag
  const regex = emojiRegex();
  return (
    text
      .replace(regex, "")
      // Variations selectors
      .replace(/[\uFE00-\uFE0F]/g, "")
      // Zero-width joiner
      .replace(/\u200D/g, "")
  );
};

/**
 * Removes Unicode formatting characters (zero-width marks) that can't be encoded in WinAnsi.
 * This includes Right-to-Left Mark (RLM), Left-to-Right Mark (LRM), and other bidirectional marks.
 *
 * Example:
 *
 * removeUnicodeFormatting("Hello‏"); // Returns "Hello"
 *  */
export const removeZeroWidthUnicodeCharacters = (text: string): string => {
  return text.replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, "");
};

/** PostgreSQL rejects U+0000 in text columns (error 22P05), e.g. bullets pasted from a PDF. */
export const stripNullBytes = <Text extends string | null | undefined>(text: Text) => {
  return typeof text === "string" ? text.replaceAll("\u0000", "") : text;
};

// https://www.30secondsofcode.org/js/s/strip-html-tags/#:~:text=All%20you%20really%20need%20is,and%20finally%20the%20closing%20bracket.
export const removeHtmlTags = (text: string) => text.replace(/<[^>]*>/g, "").trim();

export const transformStringIntoArray = (val: string, separator: string) =>
  val.split(separator).map((status) => status.trim());

export const capitalizeName = (name: string) =>
  name
    .split(" ")
    .map((word) =>
      word
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join("-")
    )
    .join(" ");

export const formatFirstName = (name: string | null | undefined): string | undefined =>
  name ? capitalizeName(name) : undefined;

export const formatLastName = (name: string | null | undefined): string | undefined =>
  name ? name.toUpperCase() : undefined;

export const formatFirstNameLastName = (firstName?: string | null, lastName?: string | null) => {
  return `${firstName ? capitalizeName(firstName) : ""}${lastName ? ` ${lastName.toUpperCase()}` : ""}`;
};

export const formatMetadata = (
  {
    metadata,
    dateStyle,
  }: {
    metadata:
      | {
          created_by?: string;
          created_at: string;
          type: "created";
        }
      | {
          updated_by?: string;
          updated_at: string;
          type: "updated";
        };
    dateStyle?: Intl.DateTimeFormatOptions["dateStyle"];
  } = {
    dateStyle: "short",
    metadata: {
      created_by: undefined,
      created_at: "",
      type: "created",
    },
  }
) => {
  const date = metadata.type === "created" ? metadata.created_at : metadata.updated_at;
  const user = metadata.type === "created" ? metadata.created_by : metadata.updated_by;
  const action = metadata.type === "created" ? "Créé" : "Modifié";

  return `${action} le ${dateFormat(date, { options: { dateStyle } })}${user ? " par" : ""}`;
};

// Trim a value and return null if it is empty
export const trimOrNull = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmedValue = String(value).trim().replace(/\s+/g, " ");
  return trimmedValue.length > 0 ? trimmedValue : null;
};

/** Prefixes the count to the singular or plural form: `pluralize(2, "mission", "missions")` → "2 missions". */
export const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count > 1 ? plural : singular}`;
